import { selectStores, selectStoresLoading } from "@/entities/partner"
import { IPartnerStore } from "@/shared/api/partners/types"
import { useAppSelector } from "@/shared/lib/redux"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"
import { selectCurrentPartner, selectPartnerStoresStatistics } from "../../model"
import CheckSvg from '@/shared/assets/images/Check.svg'
import XSvg from '@/shared/assets/images/X.svg'
import { NoTableData } from "@/shared/ui/no-table-data"
import { Loader } from "@/shared/ui/loader"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"

export const StoresTable = () => {
    const stores = useAppSelector(selectStores)
    const isLoading = useAppSelector(selectStoresLoading)
    const selectedPartner = useAppSelector(selectCurrentPartner)
    const storesStatistics = useAppSelector(selectPartnerStoresStatistics)

    const tableData = useMemo(() => {
        return Object
            .entries(stores)
            .filter((([k]) => selectedPartner ? k === selectedPartner : true))
            .reduce((acc, [_, v]) => [...acc, ...v], [])
    }, [stores, storesStatistics])

    const columns: ColumnDef<IPartnerStore, any>[] = useMemo(() => [
        {
            accessorFn: (store) => (
                <div>
                    {store.name ?? store.address ?? store.id}
                </div>
            ),
            id: 'name',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Название / id</div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full text-center">
                    {storesStatistics[store.id]?.countClients ?? 0}
                </div>
            ),
            id: 'clients',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="text-center">Количество клиентов</div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full text-center">
                    {storesStatistics[store.id]?.countTransactions ?? 0}
                </div>
            ),
            id: 'transactions',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="text-center">количество транзакций</div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full text-center">
                    {storesStatistics[store.id]?.bonusTotal ?? 0}
                </div>
            ),
            id: 'bonusTotal',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="text-center">Сумма бонусных транзакций, руб. </div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full text-center">
                    {storesStatistics[store.id]?.total ?? 0}
                </div>
            ),
            id: 'total',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="text-center">Выручка, руб.</div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full">
                    {store.isOnline
                        ? <div className="rounded-full mx-auto h-6 w-6 bg-color-primary text-white flex items-center justify-center">
                            <CheckSvg width={16} height={16} />
                        </div>
                        : <div className="rounded-full mx-auto h-6 w-6 bg-color-danger text-white flex items-center justify-center">
                            <XSvg width={16} height={16} />
                        </div>
                    }
                </div>
            ),
            id: 'isOnline',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="text-center">В работе</div>,
        },
        {
            accessorFn: (store) => (
                <div className="w-full text-end uppercase">
                    {store.lastOnline
                        ? toLocaleDateTimeString(store.lastOnline)
                        : 'Нет информации'
                    }
                </div>
            ),
            id: 'lastOnline',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-end">Последняя активность</div>,
        },
    ], [storesStatistics])

    return (
        <>
            {Boolean(tableData.length) &&
                <div className="table_box">
                    <Table columns={columns} data={tableData} />
                </div>
            }
            {(!Boolean(tableData.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(tableData.length) && !isLoading) &&
                <NoTableData label="Нет данных о торговых точках"/>
            }
        </>
    )
}